import { LockKeyIcon } from '@phosphor-icons/react'
import {
  AlertBanner,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
  Skeleton,
} from '@curri/components'
import type { ReactNode } from 'react'

import { describeFailure, type Resource } from '../../api/client.ts'
import { assertNever } from '../../assertNever.ts'
import {
  CREDENTIAL_READ_SCOPE,
  fetchAccountKeys,
  type AccountKeys,
} from '../../api/keys.ts'
import { useApiResource } from '../../api/useApiResource.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'
import { GhostBar } from '../../ghost/GhostBar.tsx'
import { useApiFetch } from '../../ghost/GhostProvider.tsx'
import { BarcodeStamp } from '../landing/Barcode.tsx'

import { BasicAuthCard } from './BasicAuthCard.tsx'
import { IdentityCard } from './IdentityCard.tsx'
import { KeysCard } from './KeysCard.tsx'

// The credentials screen: the account's API keys, the user they authenticate
// as, and the Basic Authorization header the two assemble into.
//
// Read-only for now — issuing, rotating, and OAuth app management are later
// verticals of INT-361. One endpoint feeds all three cards, so unlike the
// webhooks screen the load and failure states are handled once, up front: three
// copies of the same banner would be noise, not independence.

const useAccountKeys = (): Resource<AccountKeys> => {
  // Through the ghost-aware fetch, like every dashboard read: a Curri admin
  // viewing as a partner sees that partner's keys — a disclosure an admin
  // already has through curri-admin — and the GhostBar above the cards says so.
  const apiFetch = useApiFetch()
  return useApiResource(signal => fetchAccountKeys(apiFetch, signal), [apiFetch])
}

const Page = ({ children }: { children: ReactNode }) => (
  // The shell's pane is `overflow: hidden`, so the screen owns its own scroll.
  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
    <div
      style={{
        maxWidth: 960,
        margin: '0 auto',
        padding: '40px 32px 64px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {children}
    </div>
  </div>
)

// The design stamps section headings with a barcode rule and a plan-sheet
// serial (see landing/Barcode.tsx), same as the webhooks dashboard.
const PageHeading = () => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <h1 style={{ fontSize: 25 }}>API Keys & OAuth</h1>
      <span style={{ flex: 1 }} />
      <BarcodeStamp tone="muted" />
    </div>
    <p
      style={{
        margin: '12px 0 0',
        fontSize: 15,
        lineHeight: 1.6,
        color: 'var(--curri-text-secondary)',
        maxWidth: '62ch',
        textWrap: 'pretty',
      }}
    >
      The credentials your integration calls Curri with: your API keys, the
      user they belong to, and the Authorization header they build.
    </p>
  </div>
)

/**
 * Rendered when the session's token was issued without `credential:read`.
 *
 * Checked up front against the granted scopes rather than waiting for the 403:
 * an empty screen with an error banner reads as "your keys are gone", which is
 * the wrong diagnosis and the wrong next step.
 */
const MissingScope = () => {
  const { signOut } = useAuth()

  return (
    <Page>
      <PageHeading />
      <EmptyState variant="warning">
        <EmptyStateIcon>
          <LockKeyIcon />
        </EmptyStateIcon>
        <EmptyStateTitle>This session cannot read your API keys</EmptyStateTitle>
        <EmptyStateDescription>
          Your access token was issued without the{' '}
          <code>{CREDENTIAL_READ_SCOPE}</code> scope. Signing in again will
          request it. If it still does not appear, the OAuth client this portal
          uses needs <code>{CREDENTIAL_READ_SCOPE}</code> added to its
          registration — email engineering@curri.com.
        </EmptyStateDescription>
        <EmptyStateActions>
          <Button label="Sign out and try again" onClick={signOut} size="base" />
        </EmptyStateActions>
      </EmptyState>
    </Page>
  )
}

const Cards = ({ resource }: { resource: Resource<AccountKeys> }) => {
  switch (resource.status) {
    case 'loading':
      return (
        <>
          <Skeleton style={{ height: 168 }} />
          <Skeleton style={{ height: 168 }} />
          <Skeleton style={{ height: 168 }} />
        </>
      )
    case 'failed': {
      const copy = describeFailure(resource.failure, 'API keys')
      return (
        <AlertBanner title={copy.title} type={copy.tone}>
          {copy.body}
        </AlertBanner>
      )
    }
    case 'ready':
      // Identity first: whose keys these are is the thing to establish before
      // any of them is on screen — and under a ghost session it is the answer
      // the reader most needs to have already seen.
      return (
        <>
          <IdentityCard owner={resource.data.owner} />
          <KeysCard
            apiKey={resource.data.apiKey}
            sandboxKey={resource.data.sandboxKey}
          />
          <BasicAuthCard keys={resource.data} />
        </>
      )
    default:
      return assertNever(resource, 'resource status')
  }
}

const Dashboard = () => {
  const resource = useAccountKeys()

  return (
    <Page>
      <PageHeading />
      {/* Above the data, not beside it: whoever is reading this screen has to
          know whose keys these are before they read any of them. Renders
          nothing for a partner session. */}
      <GhostBar />
      <Cards resource={resource} />
    </Page>
  )
}

export const Credentials = () => {
  const { state } = useAuth()

  // App.tsx only routes here once authenticated, so this is a type narrowing
  // rather than a real branch.
  if (state.status !== 'authenticated') return null

  return state.caller.grantedScopes.includes(CREDENTIAL_READ_SCOPE) ? (
    <Dashboard />
  ) : (
    <MissingScope />
  )
}
